import { KING_VALUE } from 'constants/cards';
import { SolitaireGame } from 'game/SolitaireGame';
import { HintItem, HintType } from './hintGroup';
import { Card } from 'components/Card';
import { Store } from 'game/store/gameStore';
import { Column } from 'game/Column';
import { last } from 'lodash';

// ======== The priority of the available moves shows in order: =========
// General notes:
// When using the hint function repeatedly, it will show the next available move and so on.
// When we have two similar options, we show the first available from the left, to the right.
// We block a game area during a hint. You can’t stop or interrupt the hint.

// 1. Moving Cards to Form a Sequence from King to Ace
//    a. Move a card/sequence if it reveals a new card underneath.
//    b. Move a card/sequence if it frees up a column (leaving a placeholder).
//    c. Combine sequences to create a larger sequence, aiming to clear the tableau area.
// 2. Creating a Larger Sequence
//    (In the sequence where cards are moved, there should be more cards than in the sequence from which they are taken).

// ======= 1 suit
// - Move a sequence to create a larger sequence (prioritize moves that result in the largest sequence possible).
// - Move a card/sequence that reveals a new card.
// - Move a card/sequence that clears a space on the tableau (leaving a placeholder).

// ======= 2,4 suits

// - Move a card/sequence that enables the creation of a sequential order from King to Ace (on the next move)
// - Move cards to create a larger sequence (prioritize moves that form a larger sequence of a single suit).
// - Move a sequence that allows the connection of a larger sequence (larger sequence of a single suit)
// - Move a card/sequence that reveals a hidden card (regardless of the suit).
// - Move a card/sequence that clears a space on the tableau (leaving a placeholder).
// - Move a sequence to enable the creation of a larger sequence (from mixed suits).

// 3. Moving a Card to the placeholder.
//   a. Move a larger sequence to the placeholder.
//   b. Move a card/sequence that reveals a new card.
//   c. Move a King to the placeholder.
//   d. Move a larger sequence to the placeholder (to separate it from incorrect sequences)
//   e. Other cards, left to right.

// 4. Moving a Card to the Placeholder Before Dealing New Cards.
//   a. From left to right, we divide the existing sequences (starting with the largest) and move the bottom card to the placeholder.
// 5. Dealing New Cards from the Stockpile - Deal new cards when no possible moves are available.

// 6. If we don’t have any available hint, we show popup:

export function findSpiderHint(game: SolitaireGame, lastPriorityCheck: number, hintCards: string[]) {
    let hints = [] as HintItem[];

    for (let i = lastPriorityCheck; i < 9; i++) {
        if (i === 0) continue;
        switch (i) {
            case 1:
                hints = checkHint1(game, hintCards);
                break;
            case 2:
                hints = checkHint2(game, hintCards);
                break;
            case 3:
                hints = checkHint3(game, hintCards);

                break;
            case 4:
                hints = checkHint4(game, hintCards);
                break;
            case 5:
                const freeColumns = game.columns.filter((c) => c.isEmpty);
                if (freeColumns.length > 0 && lastPriorityCheck > 0) {
                    hints = [new HintItem([], freeColumns[0], HintType.RESET, 5)];
                } else {
                    hints = checkHint5(game, hintCards);
                }
                if (!hints.length && lastPriorityCheck > 0) {
                    hints = [new HintItem([], freeColumns[0], HintType.RESET, 5)];
                }
                break;
        }
        if (hints.length) {
            return hints;
        }
    }
    return hints;
}
function checkHint1(game: SolitaireGame, hintCards: string[]): HintItem[] {
    // 1. Moving Cards to Form a Sequence from King to Ace
    //    priority 1. Move a card/sequence if it reveals a new card underneath.
    //    priority 2. Move a card/sequence if it frees up a column (leaving a placeholder).
    //    priority 3. Combine sequences to create a larger sequence, aiming to clear the tableau area.

    const activeColumnsMoveGroups = game.columns
        .map((c) => c.getLastMoveGroup())
        .filter((g) => g.length > 0 && !hintCards.includes(g[0]._id));

    const startFromKingGroups = activeColumnsMoveGroups.filter((g) => g[0].value === KING_VALUE);
    const moveOption = [] as {
        targetColumn: Column;
        moveColumn: Column;
        priority: number;
        condition: number;
        moveGroup: Card[];
    }[];
    for (let index = 0; index < startFromKingGroups.length; index++) {
        const group = startFromKingGroups[index];
        const king = group[0];
        const lastCard = group[group.length - 1];

        const targetColumn = game.columns[king.place.value];
        const moveGroup = activeColumnsMoveGroups.filter((c) => {
            if (lastCard.cardColor !== c[0].cardColor) return false;
            if (c[c.length - 1].value > 1) return false;
            if (lastCard.value <= c[0].value + 1) return true;
            return false;
        });
        if (moveGroup.length) {
            const moveGroupLength = 13 - targetColumn.getLastMoveGroup().length;

            for (let i = 0; i < moveGroup.length; i++) {
                const c = moveGroup[i];
                const moveColumn = game.columns[c[0].place.value];
                const moveAll = moveColumn.getLastMoveGroup().length === moveGroupLength;

                const moveHintsCards = [...moveColumn.getLastMoveGroup()];
                while (moveHintsCards.length > moveGroupLength) {
                    moveHintsCards.shift();
                }
                if (!moveAll) {
                    moveOption.push({ targetColumn, moveColumn, priority: 1, condition: 4, moveGroup: moveHintsCards });
                    continue;
                }
                let priority = 4;
                let condition = 1;
                const nextGroup = moveColumn.getNextMovingGroup();
                const kingNextGroup = targetColumn.getNextMovingGroup();
                if (
                    (nextGroup.length === 0 && moveColumn.hasHiddenCards) ||
                    (kingNextGroup.length === 0 && targetColumn.hasHiddenCards)
                )
                    priority = 99;
                else if (
                    (nextGroup.length === 0 && !moveColumn.hasHiddenCards) ||
                    (kingNextGroup.length === 0 && !targetColumn.hasHiddenCards)
                ) {
                    priority = 90;
                    condition = 2;
                } else {
                    priority = Math.max(kingNextGroup.length, nextGroup.length);
                    condition = 3;
                }

                moveOption.push({ targetColumn, moveColumn, priority, condition, moveGroup: moveHintsCards });
            }
        }
        if (moveOption.length) {
            sortOptions(moveOption);
            const { targetColumn, condition, moveGroup } = moveOption[0];

            return [new HintItem(moveGroup, targetColumn, HintType.MOVE, 1, condition)];
        }
    }
    return [];
}
function checkHint2(game: SolitaireGame, hintCards: string[]) {
    // 2. Creating a Larger Sequence
    const moveOption = [] as {
        moveGroup: Card[];
        targetGroup: Card[];
        moveColumn: Column;
        targetColumn: Column;
        priority: number;
        condition: number;
    }[];

    const activeColumnsMoveGroups = game.columns.filter((c) => !c.isEmpty).map((c) => c.getLastMoveGroup());

    for (let index = 0; index < activeColumnsMoveGroups.length; index++) {
        const targetGroup = activeColumnsMoveGroups[index];
        const lastCard = targetGroup[targetGroup.length - 1];

        const groupContainingMatchingCard = activeColumnsMoveGroups.filter((g, gi) => {
            if (index === gi) return false; //skip the same group
            return g.find((c) => lastCard && lastCard.canDropInColumn(c));
        });

        if (groupContainingMatchingCard.length) {
            // this is the group that can be dropped on the last card of the active group, lets split it;

            for (let i = 0; i < groupContainingMatchingCard.length; i++) {
                const activeGroupThatCanBeMove = groupContainingMatchingCard[i];
                const validMoveGroup = [] as Card[];
                //build move group
                for (let j = activeGroupThatCanBeMove.length - 1; j >= 0; j--) {
                    const cardInMoveGroup = activeGroupThatCanBeMove[j];
                    validMoveGroup.push(cardInMoveGroup);
                    if (lastCard.canDropInColumn(cardInMoveGroup)) break;
                }
                validMoveGroup.reverse();

                const cardsUsed = validMoveGroup.find((c) => {
                    return hintCards.includes(c._id);
                });
                if (cardsUsed) continue;
                const moveColumn = game.columns[validMoveGroup[0].place.value];

                if (moveColumn.length > 1) {
                    if (moveColumn.cards[moveColumn.cards.length - 2].canDropInColumn(moveColumn.lastCard)) {
                        if (moveColumn.cards[moveColumn.cards.length - 2].cardColor !== moveColumn.lastCard.cardColor) {
                            continue;
                        }
                    }
                }
                const targetColumn = game.columns[targetGroup[0].place.value];

                const { priority, condition } = getPriorityForHint2(
                    validMoveGroup,
                    targetGroup,
                    moveColumn,
                    targetColumn,
                    game.columns
                );

                moveOption.push({
                    moveGroup: validMoveGroup,
                    targetGroup,
                    moveColumn,
                    targetColumn,
                    priority,
                    condition,
                });
            }
        }
    }
    if (moveOption.length) {
        sortOptions(moveOption);
        const { targetColumn, moveGroup, priority, condition } = moveOption[0];
        if (priority < 0) return [];
        return [new HintItem(moveGroup, targetColumn, HintType.MOVE, 2, condition)];
    }
    return [];
}

function checkHint3(game: SolitaireGame, hintCards: string[]) {
    // 3. Moving a Card to the placeholder.
    //   a. Move a larger sequence to the placeholder.
    //   b. Move a card/sequence that reveals a new card.
    //   c. Move a King to the placeholder.
    //   d. Move a larger sequence to the placeholder (to separate it from incorrect sequences)
    //   e. Other cards, left to right.
    const moveOption = [] as { targetColumn: Column; moveColumn: Column; priority: number; condition: number }[];

    const emptyColumns = game.columns.filter((col) => col.isEmpty);
    if (!emptyColumns.length) return [];

    const activeColumnsMoveGroups = game.columns
        .map((c) => c.getLastMoveGroup())
        .filter((g) => g.length && !hintCards.includes(g[0]._id));

    for (let index = 0; index < activeColumnsMoveGroups.length; index++) {
        const moveGroup = activeColumnsMoveGroups[index];
        const moveColumn = game.columns[moveGroup[0].place.value];
        const targetColumn = emptyColumns[0];
        let priority = 0;
        let condition = 1;

        priority = moveGroup.length * 1000;
        // Reveal new card priority
        if (moveColumn.getNextMovingGroup().length === 0 && moveColumn.hasHiddenCards) {
            priority += 20;
            condition = 2;
        }
        // King priority
        if (moveGroup[0].value === KING_VALUE) {
            priority += 10;
            condition = 3;
        }
        if (moveColumn.getNextMovingGroup().length) {
            const lastCard = moveColumn.getNextMovingGroup()[moveColumn.getNextMovingGroup().length - 1];

            if (lastCard.canDropInColumn(moveGroup[0])) {
                priority -= 10000;
                condition = 4;
            }
        }
        if (moveColumn.length > moveGroup.length) moveOption.push({ targetColumn, moveColumn, priority, condition });
    }

    if (moveOption.length) {
        sortOptions(moveOption);
        const { targetColumn, moveColumn, condition } = moveOption[0];
        return [new HintItem(moveColumn.getLastMoveGroup(), targetColumn, HintType.MOVE, 3, condition)];
    }
    return [];
}
function sortOptions(moveOption: { moveColumn: Column; targetColumn: Column; priority: number }[]) {
    moveOption.sort((a, b) => {
        if (a.priority === b.priority) {
            if (a.moveColumn.index !== b.moveColumn.index) return a.moveColumn.index < b.moveColumn.index ? -20 : 20;
            return a.targetColumn.index < b.targetColumn.index ? -10 : 10;
        }
        return a.priority > b.priority ? -30 : 30;
    });
}
function checkHint4(game: SolitaireGame, hintCards: string[]) {
    const freeColumns = game.columns.filter((c) => c.isEmpty);
    if (freeColumns.length === 0) return [];
    const activeColumnsMoveGroups = game.columns.filter(
        (g) => g.lastCard && g.length > 1 && !hintCards.includes(g.lastCard._id)
    );
    activeColumnsMoveGroups.sort((a, b) => b.length - a.length);
    if (activeColumnsMoveGroups.length) {
        const moveColumn = activeColumnsMoveGroups[0];
        if (moveColumn.lastCard) {
            return [new HintItem([moveColumn.lastCard], freeColumns[0], HintType.MOVE, 4)];
        }
    }
    return [];
}
function checkHint5(game: SolitaireGame, lastCard: string[]) {
    if (game.stockPile.lastCard && !lastCard.includes(game.stockPile.lastCard._id))
        return [new HintItem([game.stockPile.lastCard], game.stockPile, HintType.DEAL_SPIDER, 5)];
    return [];
}
function getPriorityForHint2(
    moveGroup: Card[],
    targetGroup: Card[],
    moveColumn: Column,
    targetColumn: Column,
    gameColumns: Column[]
): { priority: number; condition: number } {
    let priority = 0;
    let condition = 0;
    const moveColumnMixSuits = moveColumn.getLastCorrectOrder();

    const nextMoveGroup = moveColumn.getNextMovingGroup();
    const nextTargetGroup = targetColumn.getNextMovingGroup();
    const originMoveGroup = moveColumn.getLastMoveGroup();
    const newSequenceLength = moveGroup.length + targetGroup.length;
    if (newSequenceLength <= originMoveGroup.length) {
        //don't move if it doesn't create a larger sequence
        return { priority: -5, condition: 0 };
    }
    if (Store.suits === 1) {
        priority = newSequenceLength * 100;

        // For 1 suit, prioritize moves that create larger sequences
        condition = 1;
        // Second priority: revealing new cards
        if (nextMoveGroup.length === 0 && moveColumn.hasHiddenCards) {
            priority += 20;
            condition = 2;
        }

        // Third priority: clearing spaces
        if (nextMoveGroup.length === 0 && !moveColumn.hasHiddenCards) {
            priority += 10;
            condition = 3;
        }
    } else {
        // - Move a card/sequence that enables the creation of a sequential order from King to Ace (on the next move)
        if (nextMoveGroup.length) {
            const matchColumn = gameColumns
                .filter((c) => {
                    if (c.canDropCards(nextMoveGroup)) return true;
                    if (nextMoveGroup[nextMoveGroup.length - 1].canDropInColumn(nextMoveGroup[0])) return true;
                    return false;
                })
                .map((c) => {
                    const newLength = nextMoveGroup.length + c.getLastMoveGroup().length;
                    return newLength;
                });

            const hasCompleteSequence = matchColumn.find((c) => c === 13);
            // const sequenceColumns = gameColumns
            if (hasCompleteSequence) {
                priority = 999999;
                condition = 1;
            }
        }

        // - Move cards to create a larger sequence (prioritize moves that form a larger sequence of a single suit).
        if (moveGroup[0].cardColor === targetGroup[0].cardColor) {
            priority = 900 + newSequenceLength;
            condition = 2;
        }

        // Move a card/sequence that reveals a hidden card (regardless of the suit).
        else if (nextMoveGroup.length === 0 && moveColumn.hasHiddenCards) {
            priority += 50 + moveColumn.getLastMoveGroup()[0].value;
            condition = 3;
        }
        //  Move a card/sequence that clears a space on the tableau (leaving a placeholder).
        else if (nextMoveGroup.length === 0 && !moveColumn.hasHiddenCards) {
            priority += 20 + moveColumn.getLastMoveGroup()[0].value;
            condition = 4;
        } else {
            priority += moveColumn.getLastMoveGroup()[0].value;
            condition = 5;
        }

        if (
            nextMoveGroup.length &&
            nextMoveGroup[nextMoveGroup.length - 1].canDropInColumn(moveGroup[0]) &&
            moveGroup[0].cardColor !== targetGroup[0].cardColor &&
            newSequenceLength === nextMoveGroup.length + moveGroup.length
        ) {
            if (nextMoveGroup.length <= nextTargetGroup.length) {
                return { priority: -5, condition: 0 };
            }
        }
        if (moveGroup[0].cardColor !== targetGroup[0].cardColor) {
            if (moveColumnMixSuits.length > moveGroup.length) return { priority: -5, condition: 0 };
        }
    }
    return { priority, condition };
}
